import React, { useState, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, TextInput } from 'react-native';
import { useFocusEffect, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { apiGet, apiPost, apiDelete, api } from '../../src/api/client';
import { useToast } from '../../src/components/Toast';
import { useAuth } from '../../src/context/AuthContext';
import { useTheme } from '../../src/context/ThemeContext';
import { useNetwork } from '../../src/context/NetworkContext';
import type { ColorTheme } from '../../src/utils/theme';
import { AdminToolbar } from '../../src/components/AdminList';
import Card from '../../src/components/Card';
import ContentContainer from '../../src/components/ContentContainer';
import InlineConfirm from '../../src/components/InlineConfirm';
import ReportProblemModal from '../../src/components/ReportProblemModal';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import { rs } from '../../src/utils/responsive';
import { titleCase, formatPhone } from '../../src/utils/format';

interface ManagedAccount {
  memberId: string;
  role: string;
  accountId: string;
  companyName: string;
  status: string;
  billingEmail: string;
  autopayEnabled: boolean;
}

interface FleetMember {
  id: string;
  userId: string;
  role: string;
  name: string;
  email: string;
  phone: string | null;
  accountNumber: string;
  createdAt: string;
}

interface SearchResult {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  accountNumber: string;
}

interface FleetTransaction {
  id: string;
  washType: string;
  retailPrice: number;
  status: string;
  createdAt: string;
  locationName: string | null;
  bayName: string | null;
  userName: string | null;
  userEmail: string | null;
  userPhone: string | null;
  effectiveDiscountPercentage: number;
  discountedAmount: number;
  rating: number | null;
}

interface FleetInvoice {
  id: string;
  invoiceNumber: string;
  status: string;
  totalAmount: number;
  subtotal: number;
  discountAmount: number;
  taxAmount: number;
  dueDate: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  paidAt: string | null;
  paymentMethod: string | null;
  createdAt: string;
}

const TX_PAGE_SIZE = 10;

/** FleetManagerScreen - view fleet memberships, join/leave accounts, and manage vehicles. */
export default function FleetManagerScreen() {
  const [accounts, setAccounts] = useState<ManagedAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [members, setMembers] = useState<FleetMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [activeTab, setActiveTab] = useState<'transactions' | 'members' | 'invoices'>('transactions');
  const [transactions, setTransactions] = useState<FleetTransaction[]>([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(1);
  const [txSearch, setTxSearch] = useState('');
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [confirmRemoveMember, setConfirmRemoveMember] = useState<FleetMember | null>(null);
  const [reportTx, setReportTx] = useState<FleetTransaction | null>(null);

  // Invoices
  const [invoices, setInvoices] = useState<FleetInvoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [invTotal, setInvTotal] = useState(0);
  const [invPage, setInvPage] = useState(1);
  const [invLimit] = useState(10);
  const [invSearch, setInvSearch] = useState('');
  const invSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Member search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  const txSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toast = useToast();
  const router = useRouter();
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const { isOffline } = useNetwork();
  const { user } = useAuth();

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const data = await apiGet<ManagedAccount[]>('/api/fleet/me');
      setAccounts(data);
      if (data.length > 0 && !selectedAccountId) {
        setSelectedAccountId(data[0].accountId);
      }
    } catch (err: any) {
      setAccounts([]);
      if (!isOffline) toast.show({ type: 'error', title: 'Couldn’t load fleet accounts', message: err?.message ?? 'Try again in a moment.' });
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async (accountId: string) => {
    setLoadingMembers(true);
    try {
      const data = await apiGet<FleetMember[]>(`/api/fleet/me/accounts/${accountId}/members`);
      setMembers(data);
    } catch (err: any) {
      setMembers([]);
      if (!isOffline) toast.show({ type: 'error', title: 'Couldn’t load members', message: err?.message ?? 'Try again in a moment.' });
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchTransactions = async (accountId: string, p = 1, s = '') => {
    setLoadingTransactions(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(TX_PAGE_SIZE) });
      if (s) params.set('search', s);
      const data = await apiGet<{ transactions: FleetTransaction[]; total: number }>(`/api/fleet/me/accounts/${accountId}/transactions?${params}`);
      setTransactions(data.transactions);
      setTxTotal(data.total);
      setTxPage(p);
    } catch (err: any) {
      setTransactions([]);
      setTxTotal(0);
      if (!isOffline) toast.show({ type: 'error', title: 'Couldn’t load transactions', message: err?.message ?? 'Try again in a moment.' });
    } finally {
      setLoadingTransactions(false);
    }
  };

  const fetchInvoices = async (accountId: string, page = 1, search = '') => {
    setLoadingInvoices(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(invLimit), search });
      const data = await apiGet<{ invoices: FleetInvoice[]; total: number }>(`/api/fleet/me/accounts/${accountId}/invoices?${params}`);
      setInvoices(data.invoices);
      setInvTotal(data.total);
      setInvPage(page);
    } catch (err: any) {
      setInvoices([]);
      setInvTotal(0);
      if (!isOffline) toast.show({ type: 'error', title: 'Couldn’t load invoices', message: err?.message ?? 'Try again in a moment.' });
    } finally {
      setLoadingInvoices(false);
    }
  };

  useFocusEffect(useCallback(() => { fetchAccounts(); }, []));

  // Fetch data when selected account changes
  useFocusEffect(useCallback(() => {
    if (selectedAccountId) {
      fetchMembers(selectedAccountId);
      fetchTransactions(selectedAccountId);
      fetchInvoices(selectedAccountId);
    }
  }, [selectedAccountId]));

  const handleTxSearch = (q: string) => {
    setTxSearch(q);
    if (txSearchTimer.current) clearTimeout(txSearchTimer.current);
    txSearchTimer.current = setTimeout(() => {
      if (selectedAccountId) fetchTransactions(selectedAccountId, 1, q);
    }, 400);
  };

  const handleTxPage = (p: number) => {
    if (selectedAccountId) fetchTransactions(selectedAccountId, p, txSearch);
  };

  const handleInvSearch = (q: string) => {
    setInvSearch(q);
    if (invSearchTimer.current) clearTimeout(invSearchTimer.current);
    invSearchTimer.current = setTimeout(() => {
      if (selectedAccountId) fetchInvoices(selectedAccountId, 1, q);
    }, 400);
  };

  const handleInvPage = (p: number) => {
    if (selectedAccountId) fetchInvoices(selectedAccountId, p, invSearch);
  };

  const invoiceStatusColor = (s: string) => {
    if (s === 'paid') return '#059669';
    if (s === 'overdue') return '#dc2626';
    if (s === 'sent') return '#2563eb';
    if (s === 'cancelled') return '#6b7280';
    return '#d97706';
  };

  const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const handleSearch = async (q: string) => {
    setSearchQuery(q);
    if (q.length < 2 || !selectedAccountId) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const results = await apiGet<SearchResult[]>(
        `/api/fleet/me/search-customers?q=${encodeURIComponent(q)}&accountId=${selectedAccountId}`
      );
      setSearchResults(results);
    } catch {
      // Silent: empty results match the no-match UX. User can retype if needed.
      setSearchResults([]);
    }
    finally { setSearching(false); }
  };

  const handleAddMember = async (userId: string) => {
    if (!selectedAccountId) return;
    try {
      await apiPost(`/api/fleet/me/accounts/${selectedAccountId}/members`, { userId });
      toast.show({ type: 'success', title: 'Added', message: 'Member added to fleet' });
      setSearchQuery('');
      setSearchResults([]);
      fetchMembers(selectedAccountId);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const handleRemoveMember = (member: FleetMember) => {
    setConfirmRemoveMember(member);
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (accounts.length === 0) {
    return (
      <View style={styles.errorContainer}>
        <Ionicons name="business" size={64} color={colors.textLight} />
        <Text style={styles.errorTitle}>No Fleet Accounts</Text>
        <Text style={styles.errorText}>
          You are not assigned as a manager to any fleet accounts.
        </Text>
      </View>
    );
  }

  const selectedAccount = accounts.find(a => a.accountId === selectedAccountId);

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
      <ContentContainer>
      {/* Account Selector (if multiple) */}
      {accounts.length > 1 && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.accountTabs}>
          {accounts.map(acct => (
            <TouchableOpacity
              key={acct.accountId}
              style={[styles.accountTab, selectedAccountId === acct.accountId && styles.accountTabActive]}
              onPress={() => setSelectedAccountId(acct.accountId)}
            >
              <Text style={[styles.accountTabText, selectedAccountId === acct.accountId && styles.accountTabTextActive]}>
                {acct.companyName}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}

      {/* Account Info */}
      {selectedAccount && (
        <Card style={styles.accountCard}>
          <View style={styles.accountHeader}>
            <Ionicons name="business" size={28} color={colors.primary} />
            <View style={{ flex: 1, marginLeft: spacing.md }}>
              <Text style={styles.accountName}>{selectedAccount.companyName}</Text>
              <Text style={styles.accountType}>{selectedAccount.role === 'manager' ? 'Manager' : 'Member'}</Text>
            </View>
            <View style={[styles.statusBadge, { backgroundColor: colors.success + '20' }]}>
              <Text style={[styles.statusText, { color: colors.success }]}>{selectedAccount.status}</Text>
            </View>
          </View>
          {selectedAccount.role === 'manager' && (
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: spacing.md, paddingTop: spacing.md, borderTopWidth: 1, borderTopColor: colors.border }}>
              <View style={{ flex: 1 }}>
                <Text style={styles.memberName}>Automatic Payments</Text>
                <Text style={styles.memberDetail}>Auto-pay invoices with your default card</Text>
              </View>
              <TouchableOpacity
                onPress={async () => {
                  if (!selectedAccountId) return;
                  const next = !selectedAccount.autopayEnabled;
                  if (next) {
                    try {
                      const res = await api<{ hasPaymentMethods: boolean }>('/api/payments/has-methods');
                      if (!res.hasPaymentMethods) {
                        toast.show({ type: 'error', title: 'No Card on File', message: 'Add a payment method before enabling autopay' });
                        return;
                      }
                    } catch { /* proceed if check fails */ }
                  }
                  try {
                    await apiPost(`/api/fleet/me/accounts/${selectedAccountId}/autopay`, { enabled: next });
                    setAccounts(prev => prev.map(a => a.accountId === selectedAccountId ? { ...a, autopayEnabled: next } : a));
                    toast.show({ type: 'success', title: next ? 'Autopay Enabled' : 'Autopay Disabled' });
                  } catch (err: any) {
                    toast.show({ type: 'error', title: 'Error', message: err.message });
                  }
                }}
                style={{
                  width: 50, height: 28, borderRadius: 14,
                  backgroundColor: selectedAccount.autopayEnabled ? colors.success : colors.border,
                  justifyContent: 'center',
                  paddingHorizontal: 2,
                }}
              >
                <View style={{
                  width: 24, height: 24, borderRadius: 12,
                  backgroundColor: colors.white,
                  alignSelf: selectedAccount.autopayEnabled ? 'flex-end' : 'flex-start',
                }} />
              </TouchableOpacity>
            </View>
          )}
        </Card>
      )}

      {/* Tabs */}
      <View style={styles.tabRow}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'transactions' && styles.tabActive]}
          onPress={() => setActiveTab('transactions')}
        >
          <Ionicons name="receipt" size={16} color={activeTab === 'transactions' ? colors.primary : colors.textLight} />
          <Text style={[styles.tabText, activeTab === 'transactions' && styles.tabTextActive]}>
            Transactions
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'invoices' && styles.tabActive]}
          onPress={() => setActiveTab('invoices')}
        >
          <Ionicons name="document-text" size={16} color={activeTab === 'invoices' ? colors.primary : colors.textLight} />
          <Text style={[styles.tabText, activeTab === 'invoices' && styles.tabTextActive]}>
            Invoices
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'members' && styles.tabActive]}
          onPress={() => setActiveTab('members')}
        >
          <Ionicons name="people" size={16} color={activeTab === 'members' ? colors.primary : colors.textLight} />
          <Text style={[styles.tabText, activeTab === 'members' && styles.tabTextActive]}>
            Members
          </Text>
        </TouchableOpacity>
      </View>

      {activeTab === 'transactions' && (
        <Card style={styles.sectionCard}>
          {selectedAccount?.role === 'manager' ? (
            <AdminToolbar
              search={txSearch}
              onSearchChange={handleTxSearch}
              onSearch={() => { if (selectedAccountId) fetchTransactions(selectedAccountId, 1, txSearch); }}
              total={txTotal}
              page={txPage}
              limit={TX_PAGE_SIZE}
              onPageChange={handleTxPage}
              csvEndpoint={`/api/fleet/me/accounts/${selectedAccountId}/transactions${txSearch ? `?search=${encodeURIComponent(txSearch)}` : ''}`}
              csvFilename="fleet-transactions.csv"
              label="transaction"
            />
          ) : (
            <View style={styles.txSearchRow}>
              <View style={styles.txSearchBox}>
                <Ionicons name="search" size={16} color={colors.textLight} />
                <TextInput
                  style={styles.txSearchInput}
                  placeholder="Search transactions..."
                  placeholderTextColor={colors.textLight}
                  value={txSearch}
                  onChangeText={handleTxSearch}
                  maxLength={100}
                />
                {txSearch ? (
                  <TouchableOpacity onPress={() => { setTxSearch(''); if (selectedAccountId) fetchTransactions(selectedAccountId, 1, ''); }}>
                    <Ionicons name="close-circle" size={16} color={colors.textLight} />
                  </TouchableOpacity>
                ) : null}
              </View>
              <Text style={styles.txCount}>{txTotal} transactions</Text>
            </View>
          )}

          {loadingTransactions ? (
            <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: spacing.lg }} />
          ) : transactions.length === 0 ? (
            <Text style={styles.emptyText}>No transactions found.</Text>
          ) : (
            <>
              {transactions.map(tx => (
                <View key={tx.id} style={styles.txItem}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.memberName}>{tx.washType === 'self-serve' ? 'Self-Serve' : tx.washType}</Text>
                    {(tx.userName || tx.userEmail || tx.userPhone) ? (
                      <Text style={styles.memberDetail}>
                        {[tx.userName ? titleCase(tx.userName) : null, tx.userEmail, tx.userPhone ? formatPhone(tx.userPhone) : null].filter(Boolean).join(' · ')}
                      </Text>
                    ) : null}
                    <Text style={styles.memberDetail}>
                      {tx.locationName || 'Unknown'}{tx.bayName ? ` · ${tx.bayName}` : ''}
                    </Text>
                    <Text style={styles.memberDetail}>
                      {new Date(tx.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </Text>
                    {tx.rating != null && (
                      <View style={{ flexDirection: 'row', marginTop: 2 }}>
                        {[1, 2, 3, 4, 5].map(s => (
                          <Ionicons key={s} name={s <= tx.rating! ? 'star' : 'star-outline'} size={14} color={s <= tx.rating! ? '#f59e0b' : colors.textLight} />
                        ))}
                      </View>
                    )}
                  </View>
                  <View style={{ alignItems: 'flex-end', gap: 4 }}>
                    <Text style={styles.txAmount}>{fmt(tx.discountedAmount ?? tx.retailPrice)}</Text>
                    {tx.effectiveDiscountPercentage > 0 ? (
                      <Text style={styles.txRetail}>Retail {fmt(tx.retailPrice)} · {tx.effectiveDiscountPercentage}% off</Text>
                    ) : null}
                    <TouchableOpacity onPress={() => setReportTx(tx)} hitSlop={8}>
                      <Ionicons name="flag-outline" size={16} color={colors.textLight} />
                    </TouchableOpacity>
                  </View>
                </View>
              ))}

              {/* Pagination (members; managers page via the toolbar) */}
              {selectedAccount?.role !== 'manager' && txTotal > TX_PAGE_SIZE && (
                <View style={styles.paginationRow}>
                  <TouchableOpacity disabled={txPage <= 1} onPress={() => handleTxPage(txPage - 1)} style={{ opacity: txPage <= 1 ? 0.3 : 1 }}>
                    <Ionicons name="chevron-back" size={20} color={colors.primary} />
                  </TouchableOpacity>
                  <Text style={styles.pageText}>Page {txPage} of {Math.ceil(txTotal / TX_PAGE_SIZE)}</Text>
                  <TouchableOpacity disabled={txPage >= Math.ceil(txTotal / TX_PAGE_SIZE)} onPress={() => handleTxPage(txPage + 1)} style={{ opacity: txPage >= Math.ceil(txTotal / TX_PAGE_SIZE) ? 0.3 : 1 }}>
                    <Ionicons name="chevron-forward" size={20} color={colors.primary} />
                  </TouchableOpacity>
                </View>
              )}
            </>
          )}
        </Card>
      )}

      {activeTab === 'invoices' && (
        <Card style={styles.sectionCard}>
          {/* Search */}
          <TextInput
            style={styles.searchInput}
            placeholder="Search invoices..."
            placeholderTextColor={colors.textLight}
            value={invSearch}
            onChangeText={handleInvSearch}
            maxLength={100}
          />

          {loadingInvoices ? (
            <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: spacing.lg }} />
          ) : invoices.length === 0 ? (
            <Text style={styles.emptyText}>{invSearch ? 'No matching invoices.' : 'No invoices yet.'}</Text>
          ) : (
            <>
              {invoices.map(inv => (
                <View key={inv.id} style={styles.invoiceItem}>
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                      <Text style={styles.memberName}>{inv.invoiceNumber}</Text>
                      <View style={[styles.invBadge, { backgroundColor: invoiceStatusColor(inv.status) + '15' }]}>
                        <Text style={[styles.invBadgeText, { color: invoiceStatusColor(inv.status) }]}>{inv.status}</Text>
                      </View>
                    </View>
                    <Text style={styles.memberDetail}>
                      {new Date(inv.billingPeriodStart).toLocaleDateString()} – {new Date(inv.billingPeriodEnd).toLocaleDateString()}
                    </Text>
                    <Text style={styles.memberDetail}>
                      Due: {new Date(inv.dueDate).toLocaleDateString()}{inv.paidAt ? ` · Paid: ${new Date(inv.paidAt).toLocaleDateString()}` : ''}
                    </Text>
                  </View>
                  <View style={{ alignItems: 'flex-end', gap: 6 }}>
                    <Text style={[styles.txAmount, inv.status === 'paid' && { color: '#059669' }]}>{fmt(inv.totalAmount)}</Text>
                    <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                      <TouchableOpacity onPress={() => router.push(`/user/fleet/invoices/${inv.id}` as never)} hitSlop={8}>
                        <Ionicons name="eye-outline" size={18} color={colors.primary} />
                      </TouchableOpacity>
                      {(inv.status === 'sent' || inv.status === 'overdue') && (
                        <TouchableOpacity
                          onPress={() => router.push(`/user/fleet/invoices/${inv.id}/pay` as never)}
                          style={{ backgroundColor: colors.success, paddingHorizontal: spacing.sm, paddingVertical: 4, borderRadius: borderRadius.sm, flexDirection: 'row', alignItems: 'center', gap: 4 }}
                        >
                          <Ionicons name="card" size={14} color={colors.white} />
                          <Text style={{ color: colors.white, fontSize: fontSize.xs, fontWeight: fontWeight.semibold }}>Pay {fmt(inv.totalAmount)}</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  </View>
                </View>
              ))}
              {invTotal > invLimit && (
                <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: spacing.md, marginTop: spacing.md }}>
                  <TouchableOpacity disabled={invPage <= 1} onPress={() => handleInvPage(invPage - 1)} style={{ opacity: invPage <= 1 ? 0.3 : 1 }}>
                    <Ionicons name="chevron-back" size={20} color={colors.primary} />
                  </TouchableOpacity>
                  <Text style={styles.memberDetail}>Page {invPage} of {Math.ceil(invTotal / invLimit)}</Text>
                  <TouchableOpacity disabled={invPage >= Math.ceil(invTotal / invLimit)} onPress={() => handleInvPage(invPage + 1)} style={{ opacity: invPage >= Math.ceil(invTotal / invLimit) ? 0.3 : 1 }}>
                    <Ionicons name="chevron-forward" size={20} color={colors.primary} />
                  </TouchableOpacity>
                </View>
              )}
            </>
          )}
        </Card>
      )}

      {activeTab === 'members' && (
        <>
          {/* Search & Add Members */}
          {selectedAccount?.role === 'manager' && (
            <Card style={styles.sectionCard}>
              <Text style={styles.sectionTitle}>Add Member</Text>
              <TextInput
                style={styles.searchInput}
                placeholder="Search by name, email, or phone..."
                placeholderTextColor={colors.textLight}
                value={searchQuery}
                onChangeText={handleSearch}
                maxLength={100}
              />
              {searching && <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: spacing.sm }} />}
              {searchResults.map(user => (
                <TouchableOpacity key={user.id} style={styles.searchResultRow} onPress={() => handleAddMember(user.id)}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.memberName}>{user.name}</Text>
                    <Text style={styles.memberDetail}>{user.email}</Text>
                    {user.phone && <Text style={styles.memberDetail}>{user.phone}</Text>}
                  </View>
                  <Ionicons name="add-circle" size={28} color={colors.primary} />
                </TouchableOpacity>
              ))}
              {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
                <Text style={styles.noResultsText}>No customers found</Text>
              )}
            </Card>
          )}

          {/* Members List */}
          <Card style={styles.sectionCard}>
            {loadingMembers ? (
              <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: spacing.lg }} />
            ) : members.length === 0 ? (
              <Text style={styles.emptyText}>No members yet.</Text>
            ) : (
              members.map(member => (
                <View key={member.id} style={styles.memberItem}>
                  <View style={styles.memberIconContainer}>
                    <Ionicons
                      name={member.role === 'manager' ? 'shield-checkmark' : 'person'}
                      size={22}
                      color={member.role === 'manager' ? colors.primary : colors.textSecondary}
                    />
                  </View>
                  <View style={{ flex: 1 }}>
                    <View style={styles.memberNameRow}>
                      <Text style={styles.memberName}>{member.name}</Text>
                      {member.role === 'manager' && (
                        <View style={styles.managerBadge}>
                          <Text style={styles.managerBadgeText}>Manager</Text>
                        </View>
                      )}
                    </View>
                    <Text style={styles.memberDetail}>{member.email}</Text>
                  </View>
                  {member.role !== 'manager' && selectedAccount?.role === 'manager' && (
                    <>
                      <TouchableOpacity
                        onPress={async () => {
                          try {
                            await apiPost(`/api/fleet/me/accounts/${selectedAccountId}/managers`, { userId: member.userId });
                            toast.show({ type: 'success', title: 'Promoted', message: `${member.name} is now a manager` });
                            fetchMembers(selectedAccountId!);
                          } catch (err: any) {
                            toast.show({ type: 'error', title: 'Error', message: err.message });
                          }
                        }}
                        style={styles.removeBtn}
                        accessibilityLabel="Promote to manager"
                      >
                        <Ionicons name="shield-checkmark-outline" size={20} color={colors.primary} />
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => handleRemoveMember(member)} style={styles.removeBtn}>
                        <Ionicons name="trash-outline" size={20} color={colors.danger} />
                      </TouchableOpacity>
                    </>
                  )}
                  {member.role === 'manager' && selectedAccount?.role === 'manager' && member.userId !== user?.id && (
                    <TouchableOpacity
                      onPress={async () => {
                        try {
                          await apiDelete(`/api/fleet/me/accounts/${selectedAccountId}/managers/${member.userId}`);
                          toast.show({ type: 'success', title: 'Demoted', message: `${member.name} is now a member` });
                          fetchMembers(selectedAccountId!);
                        } catch (err: any) {
                          toast.show({ type: 'error', title: 'Error', message: err.message });
                        }
                      }}
                      style={styles.removeBtn}
                      accessibilityLabel="Demote to member"
                    >
                      <Ionicons name="remove-circle-outline" size={20} color={colors.warning} />
                    </TouchableOpacity>
                  )}
                  {confirmRemoveMember?.id === member.id && (
                    <InlineConfirm
                      message={`Remove ${member.name} from this fleet?`}
                      visible={true}
                      onConfirm={async () => {
                        try {
                          await apiDelete(`/api/fleet/me/accounts/${selectedAccountId}/members/${member.userId}`);
                          toast.show({ type: 'success', title: 'Removed', message: `${member.name} removed` });
                          setConfirmRemoveMember(null);
                          fetchMembers(selectedAccountId!);
                        } catch (err: any) {
                          toast.show({ type: 'error', title: 'Error', message: err.message });
                          setConfirmRemoveMember(null);
                        }
                      }}
                      onCancel={() => setConfirmRemoveMember(null)}
                      confirmLabel="Remove"
                    />
                  )}
                </View>
              ))
            )}
          </Card>
        </>
      )}

      <View style={{ height: spacing.xxl }} />
      </ContentContainer>

      {/* Report Problem Modal */}
      <ReportProblemModal
        visible={!!reportTx}
        onClose={() => setReportTx(null)}
        transactionId={reportTx?.id || ''}
        locationName={reportTx?.locationName || undefined}
        bayName={reportTx?.bayName || undefined}
        amount={reportTx?.retailPrice}
        date={reportTx?.createdAt}
        type={reportTx?.washType}
      />

    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  scrollContent: { padding: spacing.md },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: c.background },
  errorContainer: {
    flex: 1, justifyContent: 'center', alignItems: 'center',
    backgroundColor: c.background, padding: spacing.xl,
  },
  errorTitle: {
    fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, marginTop: spacing.md,
  },
  errorText: {
    fontSize: fontSize.md, color: c.textSecondary, textAlign: 'center',
    marginTop: spacing.sm, maxWidth: rs(320),
  },

  accountTabs: { marginBottom: spacing.md },
  accountTab: {
    paddingHorizontal: spacing.lg, paddingVertical: spacing.sm,
    borderRadius: borderRadius.md, backgroundColor: c.surface,
    marginRight: spacing.sm,
  },
  accountTabActive: { backgroundColor: c.primary },
  accountTabText: { fontSize: fontSize.sm, color: c.textSecondary, fontWeight: fontWeight.medium },
  accountTabTextActive: { color: c.white },

  accountCard: { marginBottom: spacing.md },
  accountHeader: { flexDirection: 'row', alignItems: 'center' },
  accountName: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  accountType: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: 2 },
  statusBadge: {
    paddingHorizontal: spacing.md, paddingVertical: spacing.xs, borderRadius: borderRadius.sm,
  },
  statusText: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold },

  sectionCard: { marginBottom: spacing.md },
  sectionTitle: {
    fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md,
  },

  searchInput: {
    borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.md,
    padding: spacing.sm, fontSize: fontSize.sm, color: c.text,
    backgroundColor: c.surface,
  },
  searchResultRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: spacing.md,
    borderBottomWidth: 1, borderBottomColor: c.borderLight,
  },
  noResultsText: {
    fontSize: fontSize.sm, color: c.textLight, textAlign: 'center',
    paddingVertical: spacing.md,
  },

  memberItem: {
    flexDirection: 'row', alignItems: 'center',
    padding: spacing.md,
    backgroundColor: c.surface, borderRadius: borderRadius.md,
    marginBottom: spacing.sm,
    borderWidth: 1, borderColor: c.border,
  },
  memberIconContainer: {
    width: rs(40), height: rs(40), borderRadius: rs(20),
    backgroundColor: c.primaryBg,
    justifyContent: 'center', alignItems: 'center',
    marginRight: spacing.md,
  },
  memberNameRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  memberName: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  memberDetail: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: 2 },
  managerBadge: {
    backgroundColor: c.primary + '20',
    paddingHorizontal: spacing.sm, paddingVertical: 2, borderRadius: borderRadius.sm,
  },
  managerBadgeText: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.primary },
  removeBtn: { padding: spacing.xs },
  emptyText: {
    fontSize: fontSize.sm, color: c.textLight, textAlign: 'center', paddingVertical: spacing.lg,
  },

  tabRow: {
    flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.md,
  },
  tab: {
    flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs,
    paddingVertical: spacing.sm, borderRadius: borderRadius.md,
    backgroundColor: c.surface, borderWidth: 1, borderColor: c.border,
  },
  tabActive: { backgroundColor: c.primaryBg, borderColor: c.primary },
  tabText: { fontSize: fontSize.sm, color: c.textSecondary, fontWeight: fontWeight.medium },
  tabTextActive: { color: c.primary, fontWeight: fontWeight.semibold },

  txSearchRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.sm },
  txSearchBox: {
    flex: 1, flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    backgroundColor: c.background, borderWidth: 1, borderColor: c.border,
    borderRadius: borderRadius.md, paddingHorizontal: spacing.sm, paddingVertical: 6,
    marginRight: spacing.sm,
  },
  txSearchInput: { flex: 1, fontSize: fontSize.sm, color: c.text, padding: 0 },
  txCount: { fontSize: fontSize.xs, color: c.textSecondary },
  txItem: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: spacing.md,
    borderBottomWidth: 1, borderBottomColor: c.borderLight,
  },
  txAmount: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.success },
  txRetail: { fontSize: fontSize.xs, color: c.textSecondary },
  paginationRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.lg, paddingVertical: spacing.md },
  pageText: { fontSize: fontSize.sm, color: c.textSecondary },

  // Invoice styles
  invoiceItem: {
    flexDirection: 'row', alignItems: 'center',
    paddingVertical: spacing.md,
    borderBottomWidth: 1, borderBottomColor: c.borderLight,
  },
  invBadge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8 },
  invBadgeText: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, textTransform: 'capitalize' as const },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
